<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import { page } from '$app/stores';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';

	const features = [
		{
			title: $_('header.nav.productMenu.platform.timeTracking'),
			description: $_('header.nav.productMenu.platform.timeTrackingDesc'),
			href: '/features/time-tracking',
			icon: 'clock'
		},
		{
			title: $_('header.nav.productMenu.platform.todos'),
			description: $_('header.nav.productMenu.platform.todosDesc'),
			href: '/features/todos',
			icon: 'check-square'
		},
		{
			title: $_('header.nav.productMenu.platform.tasks'),
			description: $_('header.nav.productMenu.platform.tasksDesc'),
			href: '/features/tasks',
			icon: 'check-square'
		},
		{
			title: $_('header.nav.productMenu.platform.projectMgmt'),
			description: $_('header.nav.productMenu.platform.projectMgmtDesc'),
			href: '/features/project-management',
			icon: 'folder'
		},
		{
			title: $_('header.nav.productMenu.platform.clients'),
			description: $_('header.nav.productMenu.platform.clientsDesc'),
			href: '/features/clients',
			icon: 'briefcase'
		},
		{
			title: $_('header.nav.productMenu.platform.people'),
			description: $_('header.nav.productMenu.platform.peopleDesc'),
			href: '/features/people',
			icon: 'user-check'
		},
		{
			title: $_('header.nav.productMenu.platform.teamSupport'),
			description: $_('header.nav.productMenu.platform.teamSupportDesc'),
			href: '/features/team-support',
			icon: 'users'
		},
		{
			title: $_('header.nav.productMenu.platform.pulse'),
			description: $_('header.nav.productMenu.platform.pulseDesc'),
			href: '/features/pulse',
			icon: 'zap'
		},
		{
			title: $_('header.nav.productMenu.platform.communication'),
			description: $_('header.nav.productMenu.platform.communicationDesc'),
			href: '/features/communication',
			icon: 'message-square'
		},
		{
			title: $_('header.nav.productMenu.platform.notes'),
			description: $_('header.nav.productMenu.platform.notesDesc'),
			href: '/features/notes',
			icon: 'book-open'
		},
		{
			title: $_('header.nav.productMenu.platform.files'),
			description: $_('header.nav.productMenu.platform.filesDesc'),
			href: '/features/files',
			icon: 'file'
		},
		{
			title: $_('header.nav.productMenu.platform.reports'),
			description: $_('header.nav.productMenu.platform.reportsDesc'),
			href: '/features/reports',
			icon: 'bar-chart'
		},
		{
			title: $_('header.nav.productMenu.platform.invoicing'),
			description: $_('header.nav.productMenu.platform.invoicingDesc'),
			href: '/features/invoicing',
			icon: 'file-text'
		},
		{
			title: $_('header.nav.productMenu.platform.finance'),
			description: $_('header.nav.productMenu.platform.financeDesc'),
			href: '/features/finance',
			icon: 'dollar-sign'
		},
		{
			title: $_('header.nav.productMenu.platform.absence'),
			description: $_('header.nav.productMenu.platform.absenceDesc'),
			href: '/features/absence',
			icon: 'umbrella-beach'
		}
	];
</script>

<svelte:head>
	<title>{$_('meta.features.title')} - Heyweek</title>
	<meta name="description" content={$_('meta.features.description')} />
	<meta property="og:title" content="{$_('meta.features.title')} - Heyweek" />
	<meta property="og:description" content={$_('meta.features.description')} />
</svelte:head>

<Header />

<div class="features-overview">
	<div class="container">
		<div class="features-overview__header">
			<h1 class="features-overview__title">
				{$_('features.overview.title', { default: 'All Features' })}
			</h1>
			<p class="features-overview__subtitle">
				{$_('features.overview.subtitle', {
					default: 'Everything you need to manage your work and team effectively'
				})}
			</p>
		</div>

		<div class="features-overview__grid">
			{#each features as feature}
				<a href={feature.href} class="feature-card">
					<div class="feature-card__icon">
						<Icon name={feature.icon} size="24" />
					</div>
					<h3 class="feature-card__title">{feature.title}</h3>
					<p class="feature-card__description">{feature.description}</p>
					<span class="feature-card__link">
						{$_('common.learnMore', { default: 'Learn more' })}
						<Icon name="arrow-right" size="16" />
					</span>
				</a>
			{/each}
		</div>

		<div class="features-overview__cta">
			<h2 class="features-overview__cta-title">
				{$_('features.overview.ctaTitle', { default: 'Ready to get started?' })}
			</h2>
			<p class="features-overview__cta-subtitle">
				{$_('features.overview.ctaSubtitle', {
					default: 'Join thousands of teams already using Heyweek'
				})}
			</p>
			<div class="features-overview__cta-buttons">
				<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
					{$_('hero.cta.primary')}
				</Button>
				<Button variant="secondary" size="lg" href="/pricing">
					{$_('hero.cta.secondary', { default: 'View pricing' })}
				</Button>
			</div>
		</div>
	</div>
</div>

<SeoContent slug="features" />

<Footer />

<style>
	.features-overview {
		padding: var(--space-24) 0;
		min-height: 100vh;
	}

	.features-overview__header {
		text-align: center;
		margin-bottom: var(--space-16);
	}

	.features-overview__title {
		font-size: var(--font-size-4xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.features-overview__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	.features-overview__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: var(--space-6);
		margin-bottom: var(--space-20);
	}

	.feature-card {
		display: flex;
		flex-direction: column;
		padding: var(--space-8);
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		text-decoration: none;
		transition: all 0.2s ease;
		position: relative;
	}

	.feature-card:hover {
		transform: translateY(-2px);
		box-shadow: var(--shadow-md);
		border-color: var(--border-brand);
	}

	.feature-card__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 48px;
		height: 48px;
		background: var(--bg-brand-subtle);
		color: var(--text-brand);
		border-radius: var(--radius-md);
		margin-bottom: var(--space-4);
	}

	.feature-card__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.feature-card__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		flex: 1;
		margin-bottom: var(--space-4);
	}

	.feature-card__link {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		color: var(--text-brand);
		font-weight: var(--font-weight-medium);
		font-size: var(--font-size-sm);
	}

	.features-overview__cta {
		text-align: center;
		padding: var(--space-16) 0;
	}

	.features-overview__cta-title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.features-overview__cta-subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
	}

	.features-overview__cta-buttons {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	@media (max-width: 768px) {
		.features-overview__title {
			font-size: var(--font-size-3xl);
		}

		.features-overview__grid {
			grid-template-columns: 1fr;
		}

		.features-overview__cta-buttons {
			flex-direction: column;
			align-items: center;
		}

		.features-overview__cta-buttons :global(a),
		.features-overview__cta-buttons :global(button) {
			width: 100%;
			max-width: 320px;
		}
	}
</style>
